import { z } from "zod";

const bboxAttemptArtifactsSchema = z.object({
  referenceAnnotatedUrl: z.string().optional().nullable(),
  referenceVerificationUrl: z.string().optional().nullable(),
  assetGridAnnotatedUrl: z.string().optional().nullable(),
  assetGridVerificationUrl: z.string().optional().nullable(),
});

export type BboxAttemptArtifacts = z.infer<typeof bboxAttemptArtifactsSchema>;

const motionClipArtifactsSchema = z.object({
  baseVideoAssetId: z.string().optional().nullable(),
  referenceAssetUrl: z.string().optional().nullable(),
  assetGridUrl: z.string().optional().nullable(),
  assetGridVerificationUrl: z.string().optional().nullable(),
  graphicJsonUrl: z.string().optional().nullable(),
  bboxAttempts: z.record(z.coerce.number(), bboxAttemptArtifactsSchema).optional().nullable(),
});

// V2 comparison data embedded in V3 edit
const v2ComparisonSchema = z.object({
  v2OperationId: z.string(),
  v2AssetId: z.string().optional().nullable(),
  v2VideoUrl: z.string().optional().nullable(),
  v2TimelineJsonUri: z.string().optional().nullable(),
  v2GraphicJsonUri: z.string().optional().nullable(),
  v2ShotCount: z.number(),
  v2Timeline: z.any().optional().nullable(),
  v2ToolEvalScore: z.number().optional().nullable(),
  v2ToolEvalDetails: z.any().optional().nullable(),
  v2Error: z.string().optional().nullable(),
  v3ToolEvalScore: z.number().optional().nullable(),
  comparisonWinner: z.enum(["v2", "v3", "tie"]),
  comparisonReasoning: z.string(),
  comparisonConfidence: z.number(),
  comparisonCreatedAt: z.number().optional().nullable(),
});

export const videoTypeEnumSchema = z.enum([
  "VIDEO_TYPE_UNSPECIFIED",
  "VIDEO_TYPE_TALKING_HEAD",
  "VIDEO_TYPE_VOICEOVER",
  "VIDEO_TYPE_REAL_MIRAGE",
]);

export type VideoType = z.infer<typeof videoTypeEnumSchema>;

// Where the edit was started: the Captions app or the Mirage Studio website.
export const editSurfaceSchema = z.enum(["app", "website"]);

export type EditSurface = z.infer<typeof editSurfaceSchema>;

export const EDIT_SURFACE_LABELS: Record<EditSurface, string> = {
  app: "App",
  website: "Website",
};

// URL search params of the /ai-edits/all feed.
export const aiEditsFeedSearchSchema = z.object({
  operationIds: z.string().optional(),
  userIds: z.string().optional(),
  videoTypes: z.string().optional(),
  isHorizontal: z.boolean().optional(),
  surface: editSurfaceSchema.optional(),
  orgIds: z.string().optional(),
});

export type AiEditsFeedSearch = z.infer<typeof aiEditsFeedSearchSchema>;

export const VIDEO_TYPE_LABELS: Record<VideoType, string> = {
  VIDEO_TYPE_UNSPECIFIED: "Unspecified",
  VIDEO_TYPE_TALKING_HEAD: "Talking Head",
  VIDEO_TYPE_VOICEOVER: "Voiceover",
  VIDEO_TYPE_REAL_MIRAGE: "Real Mirage",
};

const aiEditSchema = z.object({
  id: z.string(),
  assetUrl: z.string().optional().nullable(),
  baseVideoUrl: z.string().optional().nullable(),
  motionGraphicUrl: z.string().optional().nullable(),
  createdAt: z.number(),
  operationId: z.string(),
  userId: z.string(),
  videoStyleId: z.string().optional().nullable(),
  videoStyleName: z.string().optional().nullable(),
  motionEnabled: z.boolean().optional().nullable(),
  videoType: videoTypeEnumSchema.optional().nullable(),
  // Optional so the page still loads against a cap-srv that predates surfaces.
  surface: editSurfaceSchema.optional().nullable(),
  studioVideo: z
    .object({
      videoId: z.string(),
      projectId: z.string(),
      orgId: z.string().nullable(),
    })
    .optional()
    .nullable(),
  v2Comparison: v2ComparisonSchema.optional(),
});

export type AiEdit = z.infer<typeof aiEditSchema>;

export const aiEditDetailSchema = aiEditSchema.extend({
  timelineJsonUrl: z.string().optional().nullable(),
  graphicJsonUrl: z.string().optional().nullable(),
  artifactDirUrl: z.string().optional().nullable(),
  motionClips: z.record(z.string(), motionClipArtifactsSchema).optional().nullable(),
});

export type AiEditDetail = z.infer<typeof aiEditDetailSchema>;

export const aiEditsFeedResponseSchema = z.object({
  edits: z.object({
    page: z.array(aiEditSchema),
    pageInfo: z.object({
      hasNextPage: z.boolean(),
      endCursor: z.string().nullable(),
    }),
  }),
});
